import Modal from '@components/modal';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
  children?: React.ReactNode;
}

export default function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  children,
}: ConfirmationModalProps): React.JSX.Element {
  const [isProcessing, setIsProcessing] = useState(false);
  const confirmButtonClass = variant === 'danger' ? 'btn btn-danger' : 'btn btn-primary';

  const handleConfirm = async (): Promise<void> => {
    setIsProcessing(true);
    try {
      await onConfirm();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isProcessing}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={confirmButtonClass}
            onClick={() => void handleConfirm()}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : confirmLabel}
          </button>
        </>
      }
    >
      <div className="confirmation-modal__content">
        <div className={`confirmation-modal__icon confirmation-modal__icon--${variant}`}>
          <AlertTriangle size={24} />
        </div>
        <p className="confirmation-modal__message">{message}</p>
        {children}
      </div>
    </Modal>
  );
}
